import React, { useState, useEffect, useRef } from 'react';
import VoiceInput, { isVoiceInputSupported } from '../components/VoiceInput';
import { CyberBackground, NeonText, GlowingBorder } from '../components/CyberEffects';

// クイズの問題（複数回答OK）
const QUIZ_QUESTIONS = [
  { question: '赤い果物といえば？', answers: ['りんご', 'いちご', 'さくらんぼ', 'すいか', 'トマト', 'ざくろ', 'ラズベリー'] },
  { question: '黄色い食べ物といえば？', answers: ['バナナ', 'レモン', 'とうもろこし', 'たまご', 'カレー', 'チーズ', 'パイナップル', 'かぼちゃ'] },
  { question: '日本の都道府県を1つ答えて', answers: ['東京', '大阪', '北海道', '京都', '福岡', '愛知', '神奈川', '千葉', '埼玉', '兵庫', '沖縄', '広島', '宮城', '静岡', '新潟', '長野'] },
  { question: '動物の名前を1つ答えて', answers: ['犬', '猫', '象', 'ライオン', 'うさぎ', '馬', '鳥', 'さる', 'くま', 'きりん', 'パンダ', 'コアラ', 'ペンギン', 'イルカ'] },
  { question: '野菜の名前を1つ答えて', answers: ['にんじん', 'キャベツ', 'トマト', 'きゅうり', 'たまねぎ', 'じゃがいも', 'ピーマン', 'ほうれん草', '大根', 'なす', 'ブロッコリー', 'レタス'] },
  { question: '色を1つ答えて', answers: ['赤', '青', '黄色', '緑', '白', '黒', 'オレンジ', 'ピンク', '紫', '茶色', '水色', 'グレー'] },
  { question: '乗り物を1つ答えて', answers: ['車', '電車', 'バス', '飛行機', '自転車', 'バイク', '船', 'タクシー', '新幹線', 'ヘリコプター'] },
  { question: 'スポーツを1つ答えて', answers: ['サッカー', '野球', 'テニス', 'バスケ', 'バスケットボール', '水泳', 'ゴルフ', 'バレー', 'バレーボール', 'ラグビー', '卓球', 'スキー'] },
];

function VoiceTest({ navigate }) {
  const [results, setResults] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState({});

  // クイズモード用
  const [quizMode, setQuizMode] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [score, setScore] = useState({ correct: 0, wrong: 0 });
  const [lastResult, setLastResult] = useState(null); // 'correct' | 'wrong' | 'duplicate' | null
  const [isSpeaking, setIsSpeaking] = useState(false); // 読み上げ中フラグ
  const [usedAnswers, setUsedAnswers] = useState([]); // 既出の回答

  // デバッグ情報収集
  useEffect(() => {
    const info = {
      userAgent: navigator.userAgent,
      hasSpeechRecognition: !!window.SpeechRecognition,
      hasWebkitSpeechRecognition: !!window.webkitSpeechRecognition,
      isSecureContext: window.isSecureContext,
      protocol: window.location.protocol,
      // 追加のデバッグ情報
      typeofSpeechRecognition: typeof window.SpeechRecognition,
      typeofWebkitSpeechRecognition: typeof window.webkitSpeechRecognition,
      windowKeys: Object.keys(window).filter(k => k.toLowerCase().includes('speech')).join(', '),
    };
    console.log('[VoiceTest] debug info:', info);
    setDebugInfo(info);

    // 利用可能なマイクを列挙
    navigator.mediaDevices?.enumerateDevices().then(devices => {
      const mics = devices.filter(d => d.kind === 'audioinput');
      console.log('[VoiceTest] Available microphones:', mics.map(m => m.label || m.deviceId));
      setDebugInfo(prev => ({ ...prev, microphones: mics.map(m => m.label || m.deviceId.slice(0, 8)).join(', ') || 'なし' }));
    }).catch(err => {
      console.log('[VoiceTest] Cannot enumerate devices:', err);
    });
  }, []);

  const supported = isVoiceInputSupported();

  const [interimText, setInterimText] = useState('');
  const [eventLog, setEventLog] = useState([]);

  const addLog = (msg) => {
    const time = new Date().toLocaleTimeString();
    setEventLog(prev => [...prev.slice(-9), `${time}: ${msg}`]);
  };

  // ピンポン・ブー音を再生
  const playSound = (type) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    if (type === 'correct') {
      // ピンポン音（高い音2回）
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.15);
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialDecayTo && gainNode.gain.exponentialDecayTo(0.01, audioContext.currentTime + 0.4);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } else {
      // ブー音（低い音）
      oscillator.frequency.setValueAtTime(150, audioContext.currentTime);
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    }
  };

  // 次の問題を出す
  const nextQuestion = () => {
    const randomQ = QUIZ_QUESTIONS[Math.floor(Math.random() * QUIZ_QUESTIONS.length)];
    setCurrentQuestion(randomQ);
    setLastResult(null);
    setUsedAnswers([]); // 既出回答をリセット

    // 問題を読み上げ（読み上げ中はマイクをミュート）
    if ('speechSynthesis' in window) {
      setIsSpeaking(true);
      addLog('🔇 読み上げ中...');

      const utterance = new SpeechSynthesisUtterance(randomQ.question);
      utterance.lang = 'ja-JP';
      utterance.rate = 0.9;

      utterance.onend = () => {
        // 読み上げ終了後、少し待ってからマイクを有効化
        setTimeout(() => {
          setIsSpeaking(false);
          addLog('🎤 回答どうぞ！');
        }, 500);
      };

      utterance.onerror = () => {
        setIsSpeaking(false);
      };

      window.speechSynthesis.speak(utterance);
    }
  };

  // クイズモード開始
  const startQuiz = () => {
    setQuizMode(true);
    setScore({ correct: 0, wrong: 0 });
    nextQuestion();
  };

  // 回答をチェック（複数回答対応・連続テスト可能・重複チェック）
  const checkAnswer = (text) => {
    if (!currentQuestion) return;

    const normalizedInput = text.trim().toLowerCase().replace(/\s/g, '');

    // 一致した回答を探す
    const matchedAnswer = currentQuestion.answers.find(answer => {
      const normalizedAnswer = answer.toLowerCase().replace(/\s/g, '');
      return normalizedInput.includes(normalizedAnswer) || normalizedAnswer.includes(normalizedInput);
    });

    if (matchedAnswer) {
      // 重複チェック
      const normalizedMatched = matchedAnswer.toLowerCase().replace(/\s/g, '');
      if (usedAnswers.includes(normalizedMatched)) {
        setLastResult('duplicate');
        playSound('wrong');
        addLog(`🔄 重複！「${text}」は既出です`);
      } else {
        // 新しい正解
        setUsedAnswers(prev => [...prev, normalizedMatched]);
        setLastResult('correct');
        setScore(prev => ({ ...prev, correct: prev.correct + 1 }));
        playSound('correct');
        addLog(`⭕ 正解！「${text}」（${usedAnswers.length + 1}個目）`);
      }
    } else {
      setLastResult('wrong');
      setScore(prev => ({ ...prev, wrong: prev.wrong + 1 }));
      playSound('wrong');
      addLog(`❌ 不正解「${text}」（例: ${currentQuestion.answers.slice(0, 3).join(', ')}）`);
    }

    // 連続テスト：自動で次の問題に行かない
    // 1.5秒後に結果表示をリセットして次の回答を受け付ける
    setTimeout(() => {
      setLastResult(null);
    }, 1500);
  };

  const handleResult = (text) => {
    console.log('[VoiceTest] handleResult:', text, 'isSpeaking:', isSpeaking);

    // 読み上げ中の結果は無視（自分の声を拾わないため）
    if (isSpeaking) {
      console.log('[VoiceTest] Ignoring result during speech synthesis');
      addLog(`🔇 無視: ${text}（読み上げ中）`);
      return;
    }

    addLog(`結果: ${text}`);
    setResults(prev => [...prev, { text, time: new Date().toLocaleTimeString(), type: 'final' }]);
    setInterimText('');

    // クイズモードなら回答チェック
    if (quizMode && currentQuestion) {
      checkAnswer(text);
    }
  };

  const handleInterim = (text) => {
    console.log('[VoiceTest] handleInterim:', text);
    addLog(`中間: ${text}`);
    setInterimText(text);
  };

  const handleError = (err) => {
    console.log('[VoiceTest] handleError:', err);
    addLog(`エラー: ${err}`);
    setError(err);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '2rem',
    }}>
      <CyberBackground showParticles={true} />

      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '400px' }}>
        <button
          onClick={() => navigate('home')}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#888',
            cursor: 'pointer',
            marginBottom: '1rem',
          }}
        >
          ← 戻る
        </button>

        <h1 style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <NeonText color="pink" size="lg">🎤 音声認識テスト</NeonText>
        </h1>

        <GlowingBorder color="blue" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
          <p style={{ color: '#fff', marginBottom: '0.5rem' }}>
            <strong>対応状況:</strong> {supported ? '✅ 対応' : '❌ 非対応'}
          </p>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>
            {supported
              ? 'マイクボタンをタップして話しかけてください'
              : 'このブラウザは音声認識に対応していません。Chromeをお試しください。'}
          </p>
        </GlowingBorder>

        {supported && (
          <div style={{ marginBottom: '2rem' }}>
            {/* クイズモード切り替え */}
            <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
              {!quizMode ? (
                <button
                  onClick={startQuiz}
                  style={{
                    padding: '0.8rem 1.5rem',
                    background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    marginBottom: '1rem',
                  }}
                >
                  🎯 クイズモード開始
                </button>
              ) : (
                <button
                  onClick={() => { setQuizMode(false); setCurrentQuestion(null); }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#666',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  クイズ終了
                </button>
              )}
            </div>

            {/* クイズ問題表示 */}
            {quizMode && currentQuestion && (
              <GlowingBorder
                color={lastResult === 'correct' ? 'accent' : lastResult === 'wrong' ? 'pink' : 'blue'}
                style={{ padding: '1.5rem', marginBottom: '1rem', textAlign: 'center' }}
              >
                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  正解: {score.correct} / 不正解: {score.wrong}
                </p>
                <h2 style={{ color: '#fff', fontSize: '1.5rem', marginBottom: '1rem' }}>
                  {currentQuestion.question}
                </h2>
                {lastResult && (
                  <p style={{
                    fontSize: '2rem',
                    color: lastResult === 'correct' ? '#2ecc71' : lastResult === 'duplicate' ? '#f39c12' : '#e74c3c',
                    animation: 'pulse 0.5s'
                  }}>
                    {lastResult === 'correct' ? '⭕ ピンポン！' : lastResult === 'duplicate' ? '🔄 重複！' : '❌ ブー！'}
                  </p>
                )}
                {lastResult === 'wrong' && (
                  <p style={{ color: '#f1c40f', marginTop: '0.5rem' }}>
                    例: {currentQuestion.answers.slice(0, 4).join('、')}など
                  </p>
                )}
                {usedAnswers.length > 0 && (
                  <p style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                    回答済み: {usedAnswers.length}個
                  </p>
                )}
                <p style={{ color: '#aaa', fontSize: '0.8rem', marginTop: '1rem' }}>
                  💡 同じ問題に何度でも答えられます
                </p>
                <button
                  onClick={nextQuestion}
                  disabled={isSpeaking}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1.5rem',
                    background: isSpeaking ? '#444' : 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    cursor: isSpeaking ? 'not-allowed' : 'pointer',
                  }}
                >
                  ➡️ 次の問題
                </button>
              </GlowingBorder>
            )}

            <VoiceInput
              onResult={handleResult}
              onInterim={handleInterim}
              disabled={isSpeaking}
              autoStart={quizMode && !isSpeaking}
              keepListening={!isSpeaking}
            />
            {isSpeaking && (
              <p style={{ color: '#ff9800', marginTop: '0.5rem', textAlign: 'center' }}>
                🔇 読み上げ中...（終わったら話してください）
              </p>
            )}
            {/* 中間結果表示 */}
            {interimText && (
              <p style={{ color: '#f1c40f', marginTop: '0.5rem', fontSize: '1.1rem', textAlign: 'center' }}>
                認識中: {interimText}
              </p>
            )}
            {error && (
              <p style={{ color: '#ff6b6b', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                エラー: {error}
              </p>
            )}
          </div>
        )}

        <GlowingBorder color="accent" style={{ padding: '1rem' }}>
          <h3 style={{ color: '#00d4ff', marginBottom: '0.5rem' }}>認識結果:</h3>
          {results.length === 0 ? (
            <p style={{ color: '#666' }}>まだ結果がありません</p>
          ) : (
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {results.map((r, i) => (
                <div
                  key={i}
                  style={{
                    padding: '0.5rem',
                    borderBottom: '1px solid #333',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ color: '#fff' }}>{r.text}</span>
                  <span style={{ color: '#666', fontSize: '0.8rem' }}>{r.time}</span>
                </div>
              ))}
            </div>
          )}
        </GlowingBorder>

        <div style={{ marginTop: '2rem', color: '#888', fontSize: '0.85rem' }}>
          <p><strong>ヒント:</strong></p>
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li>Safari(Mac)は非対応です</li>
            <li>Chrome/Edgeを推奨します</li>
            <li>iPhoneはSafariでも一部対応</li>
            <li>マイクの許可を確認してください</li>
          </ul>
        </div>

        {/* イベントログ */}
        <GlowingBorder color="purple" style={{ padding: '1rem', marginTop: '1rem' }}>
          <h3 style={{ color: '#aa00ff', marginBottom: '0.5rem', fontSize: '0.9rem' }}>イベントログ:</h3>
          <div style={{ fontSize: '0.7rem', color: '#aaa', maxHeight: '150px', overflowY: 'auto' }}>
            {eventLog.length === 0 ? (
              <p>イベントなし</p>
            ) : (
              eventLog.map((log, i) => <p key={i}>{log}</p>)
            )}
          </div>
        </GlowingBorder>

        {/* デバッグ情報 */}
        <GlowingBorder color="pink" style={{ padding: '1rem', marginTop: '1rem' }}>
          <h3 style={{ color: '#ff00aa', marginBottom: '0.5rem', fontSize: '0.9rem' }}>デバッグ情報:</h3>
          <div style={{ fontSize: '0.75rem', color: '#888', textAlign: 'left' }}>
            <p>SpeechRecognition: {debugInfo.hasSpeechRecognition ? '✅' : '❌'} ({debugInfo.typeofSpeechRecognition})</p>
            <p>webkitSpeechRecognition: {debugInfo.hasWebkitSpeechRecognition ? '✅' : '❌'} ({debugInfo.typeofWebkitSpeechRecognition})</p>
            <p>SecureContext: {debugInfo.isSecureContext ? '✅' : '❌'}</p>
            <p>Protocol: {debugInfo.protocol}</p>
            <p>Speech関連: {debugInfo.windowKeys || 'なし'}</p>
            <p>マイク: {debugInfo.microphones || '取得中...'}</p>
            <p style={{ wordBreak: 'break-all', marginTop: '0.5rem' }}>
              UA: {debugInfo.userAgent?.substring(0, 150)}
            </p>
          </div>
        </GlowingBorder>
      </div>
    </div>
  );
}

export default VoiceTest;
